/**
 * Entry point: main menu → character select → single-player match.
 *
 * Gameplay lives in the deterministic shared sim; the client renders its
 * entities (player, bot, projectiles, pickups) and feeds input. The sim only
 * advances while a match is in progress — the arena renders as a menu backdrop.
 */
import { Scene } from "@babylonjs/core/scene.js";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight.js";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight.js";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color.js";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder.js";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial.js";
import type { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh.js";

import { ARENA_HALF_EXTENT, ARENAS, length, type EntityId } from "@allstars/shared";
import { createEngine, type Backend } from "./render/engine.js";
import { createFollowCamera } from "./render/followCamera.js";
import { createCharacter } from "./assets/character.js";
import { createBot, type Bot } from "./assets/bot.js";
import { createObstacles } from "./assets/obstacles.js";
import { createProjectilePool } from "./assets/projectile.js";
import { createPickup, type Pickup } from "./assets/pickup.js";
import { createLocalGame } from "./game/localGame.js";
import { createInput } from "./input/index.js";
import { createMenu } from "./ui/menu.js";

const canvas = document.getElementById("renderCanvas") as HTMLCanvasElement;
const hud = document.getElementById("hud") as HTMLDivElement;

const showFatal = (context: string, err: unknown): void => {
  const message = err instanceof Error ? `${err.message}\n\n${err.stack ?? ""}` : String(err);
  const box = document.createElement("pre");
  box.style.cssText =
    "position:fixed;inset:0;margin:0;padding:16px;z-index:9999;" +
    "background:#1a0d0d;color:#ff9b9b;font:12px/1.5 ui-monospace,Menlo,monospace;" +
    "white-space:pre-wrap;overflow:auto;-webkit-user-select:text;user-select:text;";
  box.textContent = `AllStars — ${context}\n\n${message}`;
  document.body.appendChild(box);
};

window.addEventListener("error", (e) => showFatal("uncaught error", e.error ?? e.message));
window.addEventListener("unhandledrejection", (e) => showFatal("unhandled rejection", e.reason));

const buildEnvironment = (scene: Scene): void => {
  scene.clearColor = new Color4(0.043, 0.055, 0.078, 1);
  const ambient = new HemisphericLight("ambient", new Vector3(0, 1, 0), scene);
  ambient.intensity = 0.7;
  const sun = new DirectionalLight("sun", new Vector3(-0.4, -1, -0.6), scene);
  sun.intensity = 1.1;

  const ground = MeshBuilder.CreateGround(
    "arena",
    { width: ARENA_HALF_EXTENT * 2, height: ARENA_HALF_EXTENT * 2 },
    scene,
  );
  const groundMat = new StandardMaterial("groundMat", scene);
  groundMat.diffuseColor = new Color3(0.16, 0.2, 0.29);
  groundMat.specularColor = new Color3(0.05, 0.05, 0.05);
  ground.material = groundMat;
};

const startHud = (
  getFps: () => number,
  getHp: () => number,
  backend: Backend,
  note?: string,
): void => {
  const label = backend === "webgpu" ? "WebGPU" : "WebGL2";
  const suffix = note ? `  ·  ${note}` : "";
  setInterval(() => {
    hud.textContent =
      `AllStars  ·  ${label}  ·  ${getFps().toFixed(0)} FPS` +
      `  ·  HP ${Math.ceil(getHp())}${suffix}`;
  }, 250);
};

type AppState = "menu" | "playing" | "result";

const main = async (): Promise<void> => {
  const { engine, backend, note } = await createEngine(canvas);
  const scene = new Scene(engine);
  buildEnvironment(scene);

  const cam = createFollowCamera(scene);
  const character = await createCharacter(scene);
  const pool = createProjectilePool(scene);
  const game = createLocalGame();
  const input = createInput(scene, document.body, () => ({
    x: character.worldPos.x,
    z: character.worldPos.z,
  }));

  // Static arena cover + pickup meshes (one arena for now).
  createObstacles(scene, ARENAS[game.arenaId()].obstacles);
  const pickupMeshes: Pickup[] = game
    .pickups()
    .map((pk) => createPickup(scene, pk.pos.x, pk.pos.y));

  cam.follow(character.worldPos, 1);

  const botViews = new Map<EntityId, Bot>();
  const projViews = new Map<EntityId, InstancedMesh>();
  const clearDynamicViews = (): void => {
    for (const b of botViews.values()) b.dispose();
    botViews.clear();
    for (const inst of projViews.values()) pool.release(inst);
    projViews.clear();
  };

  let facing = 0;
  let appState: AppState = "menu";

  const menu = createMenu();
  menu.onStart((charId) => {
    clearDynamicViews();
    game.start(charId);
    appState = "playing";
  });
  menu.onReplay(() => {
    clearDynamicViews();
    game.reset();
    appState = "playing";
  });
  menu.onBackToMenu(() => {
    clearDynamicViews();
    game.reset();
    appState = "menu";
    menu.showMenu();
  });
  menu.showMenu();

  const renderEntities = (alpha: number): void => {
    const liveBots = new Set<EntityId>();
    const liveProj = new Set<EntityId>();
    for (const { id, kind } of game.entities()) {
      const p = game.interpolatedTransform(id, alpha);
      if (kind === "player") {
        character.setGroundPosition(p.x, p.y);
        const h = game.healthOf(id);
        if (h) character.setHealth(h.current / h.max);
      } else if (kind === "bot") {
        liveBots.add(id);
        let view = botViews.get(id);
        if (!view) {
          view = createBot(scene);
          botViews.set(id, view);
        }
        view.setGroundPosition(p.x, p.y);
        const aim = game.aimOf(id);
        if (length(aim) > 0.01) view.setFacing(Math.atan2(aim.x, aim.y));
        const h = game.healthOf(id);
        if (h) view.setHealth(h.current / h.max);
      } else {
        liveProj.add(id);
        let inst = projViews.get(id);
        if (!inst) {
          const acquired = pool.acquire();
          if (acquired) {
            inst = acquired;
            projViews.set(id, acquired);
          }
        }
        if (inst) inst.position.set(p.x, 0.6, p.y);
      }
    }
    for (const [id, view] of botViews) {
      if (!liveBots.has(id)) {
        view.dispose();
        botViews.delete(id);
      }
    }
    for (const [id, inst] of projViews) {
      if (!liveProj.has(id)) {
        pool.release(inst);
        projViews.delete(id);
      }
    }
  };

  engine.runRenderLoop(() => {
    const dt = Math.min(engine.getDeltaTime() / 1000, 0.1);
    let alpha = 0;

    if (appState === "playing") {
      const sample = input.read();
      alpha = game.advance(dt, sample);

      if (length(sample.aim) > 0.05) facing = Math.atan2(sample.aim.x, sample.aim.y);
      else if (length(sample.move) > 0.05) facing = Math.atan2(sample.move.x, sample.move.y);
      character.setFacing(facing);
      character.setAiming(length(sample.aim) > 0.05);
      character.update(game.playerSpeed(), dt);

      const outcome = game.outcome();
      if (outcome !== "playing") {
        appState = "result";
        character.setAiming(false);
        menu.showResult(outcome);
      }
    } else {
      character.setAiming(false);
    }

    renderEntities(alpha);

    const pickupState = game.pickups();
    pickupMeshes.forEach((mesh, i) => {
      const s = pickupState[i];
      mesh.setActive(s ? s.active : false);
      mesh.spin(dt);
    });

    cam.follow(character.worldPos, dt);
    scene.render();
  });

  window.addEventListener("resize", () => engine.resize());
  startHud(
    () => engine.getFps(),
    () => game.healthOf(game.playerId)?.current ?? 0,
    backend,
    note,
  );

  // eslint-disable-next-line no-console
  console.info(`[AllStars] render backend: ${backend}${note ? ` (${note})` : ""}`);
};

main().catch((err) => showFatal("boot failed", err));
